import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

import AuditService from '../../services/audit.service';
import Spinner from '../../components/ui/spinner.component';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AuditView() {
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [operationFilter, setOperationFilter] = useState('all');
  const [successFilter, setSuccessFilter] = useState('all');
  const [operationTypes, setOperationTypes] = useState([]);

  const { loading, setLoading, hasMore, setHasMore, setupScrollListener, resetScroll } = useInfiniteScroll();

  const fetchLogs = useCallback(async (page = 1, reset = false, operationOverride = null, successOverride = null) => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await AuditService.getAuditLogs({
        page,
        per_page: 50,
        operation: operationOverride !== null ? operationOverride : operationFilter,
        success: successOverride !== null ? successOverride : successFilter
      });

      const { logs: newLogs, pagination } = response.data;

      if (reset) {
        setLogs(newLogs);
        setCurrentPage(1);
      } else {
        setLogs(prev => page === 1 ? newLogs : [...prev, ...newLogs]);
      }

      setHasMore(pagination.has_next);
      setTotalLogs(pagination.total);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Er is een fout opgetreden bij het laden van de audit logs.');
    } finally {
      setLoading(false);
    }
  }, [loading, operationFilter, successFilter]);

  const loadMoreLogs = useCallback(() => {
    if (hasMore && !loading) {
      fetchLogs(currentPage + 1);
    }
  }, [fetchLogs, hasMore, loading, currentPage]);

  const handleOperationFilter = useCallback((operation) => {
    setOperationFilter(operation);
    resetScroll();
    setCurrentPage(1);
    fetchLogs(1, true, operation);
  }, [resetScroll, fetchLogs]);

  const handleSuccessFilter = useCallback((success) => {
    setSuccessFilter(success);
    resetScroll();
    setCurrentPage(1);
    fetchLogs(1, true, null, success);
  }, [resetScroll, fetchLogs]);

  useEffect(() => {
    document.title = "Assemblo - Admin - Audit Log";
    fetchLogs(1, true);

    // Load operation types for filter
    AuditService.getOperationTypes()
      .then(response => setOperationTypes(response.data))
      .catch(error => console.error('Error loading operation types:', error));
  }, []);

  useEffect(() => {
    const cleanup = setupScrollListener(loadMoreLogs);
    return cleanup;
  }, [loadMoreLogs]);

  function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('nl-BE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  function getOperationLabel(operation) {
    const labels = {
      'clean_system': 'Systeem opschonen',
      'clean_system_dry_run': 'Systeem opschonen (test)',
      'clean_system_mockup': 'Systeem opschonen (mockup)',
      'export_system': 'Data export',
      'update_login_mail': 'Login e-mail aangepast',
      'set_pin': 'PIN instellen',
      'fail2ban_block': 'IP blokkeren',
      'fail2ban_unblock': 'IP deblokkeren'
    };
    return labels[operation] || operation;
  }

  function formatDetails(details) {
    if (!details) return null;
    if (typeof details === 'string') return details;

    // Format common detail types
    if (details.deletion_counts) {
      return (
        <div className="text-xs text-gray-600 mt-1">
          <div>Verwijderd: {details.deletion_counts.meetings} meetings, {details.deletion_counts.users} users, {details.deletion_counts.votes} votes</div>
        </div>
      );
    }

    if (details.target_user_email) {
      return (
        <div className="text-xs text-gray-600 mt-1">
          Voor: {details.target_user_email}
        </div>
      );
    }

    if (details.count !== undefined) {
      return (
        <div className="text-xs text-gray-600 mt-1">
          Aantal: {details.count}
        </div>
      );
    }

    if (details.filename) {
      return (
        <div className="text-xs text-gray-600 mt-1">
          Bestand: {details.filename}
        </div>
      );
    }

    if (details.reason) {
      return (
        <div className="text-xs text-red-600 mt-1">
          Reden: {details.reason}
        </div>
      );
    }

    return null;
  }

  return (
    <main className="flex-1">
      <div className="py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center">
            <h1 className="text-2xl font-semibold text-gray-900">Audit Log</h1>
            {loading && (
              <div className="ml-3">
                <Spinner size="small" />
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto px-4 sm:px-6 md:px-8">
          <p className="mt-2 text-sm text-gray-500">
            Overzicht van alle systeem operaties en beveiligingsgebeurtenissen
          </p>

          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <div className="p-4 bg-white border-b border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700">Filters</h3>
                      <div className="text-sm text-gray-600">
                        {totalLogs} log entr{totalLogs !== 1 ? 'ies' : 'y'}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {/* Operation Filter */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">Operatie:</span>
                        <button
                          onClick={() => handleOperationFilter('all')}
                          disabled={loading}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            operationFilter === 'all'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          Alle
                        </button>
                        {operationTypes.map((op) => (
                          <button
                            key={op}
                            onClick={() => handleOperationFilter(op)}
                            disabled={loading}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                              operationFilter === op
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {getOperationLabel(op)}
                          </button>
                        ))}
                      </div>

                      {/* Success Filter */}
                      <div className="flex items-center space-x-2 ml-4">
                        <span className="text-xs font-medium text-gray-500">Status:</span>
                        <button
                          onClick={() => handleSuccessFilter('all')}
                          disabled={loading}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            successFilter === 'all'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          Alle
                        </button>
                        <button
                          onClick={() => handleSuccessFilter('true')}
                          disabled={loading}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            successFilter === 'true'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          Geslaagd
                        </button>
                        <button
                          onClick={() => handleSuccessFilter('false')}
                          disabled={loading}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            successFilter === 'false'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          Gefaald
                        </button>
                      </div>
                    </div>
                  </div>

                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Status
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Tijdstip
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Gebruiker
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Operatie
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          IP Adres
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {logs.map((log, logIdx) => (
                        <tr key={log.id} className={logIdx % 2 === 0 ? undefined : 'bg-gray-50'}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            {log.success ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                              {formatDate(log.created_on)}
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900">
                            <div className="font-medium">{log.user_name}</div>
                            <div className="text-xs text-gray-500">{log.user_email}</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                            {getOperationLabel(log.operation)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                            {log.ip_address || '-'}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            {log.error_message ? (
                              <div className="text-red-600 text-xs">
                                Fout: {log.error_message}
                              </div>
                            ) : (
                              formatDetails(log.details)
                            )}
                          </td>
                        </tr>
                      ))}
                      {!loading && logs.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center py-8 text-gray-500">
                            Geen audit logs gevonden
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
